// The service keys of a game or of the organization (spec/dashboard.md section 12.4; spec/api-conventions.md section 2):
// rows `key-<id>`, Create… (`create-key-dialog`) and Rotate… (`rotate-key-dialog`) hand the new secret to `SecretOnce`,
// the one place it is ever shown; Revoke confirms. Secrets are write-only: the grid shows `prefix…hint`. A game key can be
// bound to one branch as well (M7 slice 3: the Branch field; the binding column names the branch).
import { useEffect, useState, type FormEvent } from 'react';
import type { ServiceKey, ServiceKeyCreated } from '../../api/types';
import { Button } from '../../ui/Button';
import { Badge, Chip } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { Checkbox, Select, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import type { ZoneContext } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { DateTimeInput } from '../../ui/values';
import { asApiError, DEFAULT_GRACE_SECONDS, keyLive, keyStatus, lastUsedText, SCOPES, secondsToIso } from './keys';
import { SecretOnce } from './SecretOnce';
import '../org/org.css';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import { EmptyState } from '../../ui/Banner';
import { KeyRound, Plus } from '../../ui/icons';

export interface CreateKeyBody {
  name: string;
  scopes: string[];
  environment?: string;
  /** a game key bound to one branch (its name; M7 slice 3) */
  branch?: string;
  expiresAt?: string;
}

export interface KeysPanelProps {
  rows: ServiceKey[];
  loading?: boolean;
  missing: string | null;
  /** the game's environments for the binding; null on the organization (no environment binding) */
  environments: { id: string; slug: string }[] | null;
  /** the game's branches for the branch binding; null / absent on the organization (no branch binding) */
  branches?: { id: string; name: string }[] | null;
  /** what an unbound key covers: "the whole game" / "the whole organization" */
  wholeLabel: string;
  when: (v: string | null | undefined) => string;
  ctx?: ZoneContext;
  onCreate: (body: CreateKeyBody, idempotencyKey: string) => Promise<ServiceKeyCreated>;
  onRotate: (k: ServiceKey, graceSeconds: number) => Promise<ServiceKeyCreated>;
  onRevoke: (k: ServiceKey) => Promise<void>;
}

export function KeysPanel({ rows, loading, missing, environments, branches = null, wholeLabel, when, ctx, onCreate, onRotate, onRevoke }: KeysPanelProps) {
  const toasts = useToasts();
  const confirm = useConfirm();
  const [creating, setCreating] = useState(false);
  const [rotating, setRotating] = useState<ServiceKey | null>(null);
  const [secret, setSecret] = useState<{ title: string; token: string; hint: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const binding = (k: ServiceKey) => {
    const parts: string[] = [];
    if (k.environmentId) parts.push(environments?.find((e) => e.id === k.environmentId)?.slug ?? k.environmentId);
    if (k.branchId) parts.push(`branch ${branches?.find((b) => b.id === k.branchId)?.name ?? k.branchId}`);
    return parts.length ? parts.join(' · ') : wholeLabel;
  };

  const revoke = async (k: ServiceKey) => {
    if (!(await confirm({ title: `Revoke key ${k.name}?`, body: 'Anything using it stops authenticating at once.', confirmLabel: 'Revoke', tone: 'danger' }))) return;
    setBusy(k.id);
    try {
      await onRevoke(k);
      toasts.success(`Key ${k.name} revoked.`);
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="pane settings-block">
      <div className="pane-head">
        <h2>Service keys</h2>
        {rows.length > 0 && <span className="muted">{rows.length}</span>}
        <div className="spacer" />
        <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={() => setCreating(true)} missingPermission={missing}>
          Create…
        </Button>
      </div>
      <div className="pane-body flush">
      {loading && rows.length === 0 ? (
        <SkeletonRows />
      ) : rows.length === 0 ? (
        <EmptyState icon={<KeyRound />} title="No service keys yet" body="Create one for a build server, a CI job or a backend." />
      ) : (
        <table className="pane-table settings-table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Key</th>
              <th scope="col">Scopes</th>
              <th scope="col">Binding</th>
              <th scope="col">Expires</th>
              <th scope="col">Last used</th>
              <th scope="col">Status</th>
              <th scope="col">Created</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((k) => {
              const status = keyStatus(k, when);
              const live = keyLive(k);
              return (
                <tr key={k.id} data-testid={`key-${k.id}`}>
                  <td className="key-name">{k.name}</td>
                  <td>
                    <code>
                      {k.prefix}…{k.hint}
                    </code>
                  </td>
                  <td>
                    <span className="settings-chips">
                      {k.scopes.map((s) => (
                        <Chip key={s} outline>
                          {s}
                        </Chip>
                      ))}
                    </span>
                  </td>
                  <td>{binding(k)}</td>
                  <td>{k.expiresAt ? when(k.expiresAt) : 'never'}</td>
                  <td data-testid={`last-used-${k.id}`}>
                    <LastUsed at={k.lastUsedAt} when={when} />
                  </td>
                  <td>
                    <Badge tone={status.tone}>{status.word}</Badge>
                  </td>
                  <td>{when(k.createdAt)}</td>
                  <td className="settings-actions actions">
                    {live && (
                      <>
                        <Button size="s" onClick={() => setRotating(k)} missingPermission={missing} disabled={Boolean(k.rotationGraceUntil)} title={k.rotationGraceUntil ? 'This key was already rotated; rotate its successor.' : undefined}>
                          Rotate…
                        </Button>
                        <Button size="s" variant="danger" onClick={() => void revoke(k)} missingPermission={missing} busy={busy === k.id}>
                          Revoke
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      </div>
      <CreateKeyDialog
        open={creating}
        environments={environments}
        branches={branches}
        wholeLabel={wholeLabel}
        ctx={ctx}
        onClose={() => setCreating(false)}
        onCreate={onCreate}
        onCreated={(r) => {
          setCreating(false);
          setSecret({ title: `Key ${r.key.name} created`, token: r.token, hint: r.key.hint });
        }}
      />
      <RotateKeyDialog
        target={rotating}
        onClose={() => setRotating(null)}
        onRotate={onRotate}
        onRotated={(r) => {
          setRotating(null);
          setSecret({ title: `Key ${r.key.name} rotated`, token: r.token, hint: r.key.hint });
        }}
      />
      <SecretOnce open={secret !== null} title={secret?.title} label="Key" secret={secret?.token ?? ''} hint={secret?.hint} onClose={() => setSecret(null)} />
    </div>
  );
}

function CreateKeyDialog({ open, environments, branches, wholeLabel, ctx, onClose, onCreate, onCreated }: { open: boolean; environments: { id: string; slug: string }[] | null; branches: { id: string; name: string }[] | null; wholeLabel: string; ctx?: ZoneContext; onClose: () => void; onCreate: KeysPanelProps['onCreate']; onCreated: (r: ServiceKeyCreated) => void }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [environment, setEnvironment] = useState('');
  const [branch, setBranch] = useState('');
  const [expires, setExpires] = useState<number | null>(null);
  const [error, setError] = useState<ReturnType<typeof asApiError> | null>(null);
  const [busy, setBusy] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    if (open) {
      setName('');
      setScopes([]);
      setEnvironment('');
      setBranch('');
      setExpires(null);
      setError(null);
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [open]);

  const toggle = (s: string, on: boolean) => setScopes((cur) => (on ? SCOPES.filter((x) => x === s || cur.includes(x)) : cur.filter((x) => x !== s)));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: CreateKeyBody = { name: name.trim(), scopes };
      if (environment) body.environment = environment;
      if (branch) body.branch = branch;
      const iso = secondsToIso(expires);
      if (iso) body.expiresAt = iso;
      onCreated(await onCreate(body, idempotencyKey));
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Create a service key" open={open} onClose={onClose} size="m">
      <form onSubmit={(e) => void submit(e)} data-testid="create-key-dialog">
        <Field label="Name" required error={error?.fieldMessage('name')}>
          <TextInput aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" placeholder="What will use it" />
        </Field>
        <fieldset className="settings-scopes">
          <legend>Scopes</legend>
          {SCOPES.map((s) => (
            <Checkbox key={s} label={s} aria-label={s} checked={scopes.includes(s)} onChange={(e) => toggle(s, e.target.checked)} />
          ))}
          {error?.fieldMessage('scopes') && <div className="field-error">{error.fieldMessage('scopes')}</div>}
        </fieldset>
        {environments && (
          <Field label="Environment" help="A key bound to one environment is refused by the others." error={error?.fieldMessage('environment')}>
            <Select aria-label="Environment" value={environment} onChange={(e) => setEnvironment(e.target.value)}>
              <option value="">{wholeLabel}</option>
              {environments.map((env) => (
                <option key={env.id} value={env.slug}>
                  {env.slug}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {branches && (
          <Field label="Branch" help="Optional: a key bound to one branch is refused by the others' routes and cannot push another ref." error={error?.fieldMessage('branch')}>
            <Select aria-label="Branch" value={branch} onChange={(e) => setBranch(e.target.value)}>
              <option value="">every branch</option>
              {branches.map((b) => (
                <option key={b.id} value={b.name}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Expires" help="Optional; empty never expires." error={error?.fieldMessage('expiresAt')}>
          <DateTimeInput aria-label="Expires" allowEmpty ctx={ctx} value={expires} onCommit={setExpires} />
        </Field>
        {error && !error.details?.length && (
          <LoadError>{error.message}</LoadError>
        )}
        <div className="modal-footer form-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={busy} disabled={!name.trim() || scopes.length === 0}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RotateKeyDialog({ target, onClose, onRotate, onRotated }: { target: ServiceKey | null; onClose: () => void; onRotate: KeysPanelProps['onRotate']; onRotated: (r: ServiceKeyCreated) => void }) {
  const [grace, setGrace] = useState(String(DEFAULT_GRACE_SECONDS));
  const [error, setError] = useState<ReturnType<typeof asApiError> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target) {
      setGrace(String(DEFAULT_GRACE_SECONDS));
      setError(null);
    }
  }, [target]);
  const valid = /^\d+$/.test(grace.trim());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!target || !valid) return;
    setBusy(true);
    setError(null);
    try {
      onRotated(await onRotate(target, Number(grace.trim())));
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={target ? `Rotate key ${target.name}` : 'Rotate key'} open={target !== null} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} data-testid="rotate-key-dialog">
        <p className="small">A new secret replaces this one; the old secret keeps working until the grace period ends.</p>
        <Field label="Grace seconds" help="How long the old secret keeps working (at most 7 days)." error={error?.fieldMessage('graceSeconds')}>
          <TextInput type="number" min={0} aria-label="Grace seconds" value={grace} onChange={(e) => setGrace(e.target.value)} aria-invalid={!valid || undefined} />
        </Field>
        {error && !error.details?.length && (
          <LoadError>{error.message}</LoadError>
        )}
        <div className="modal-footer form-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={busy} disabled={!valid}>
            Rotate
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** "3h ago" (the exact instant beside it, in the display zone), or "never" (spec/api-conventions.md section 21.1). */
export function LastUsed({ at, when }: { at: string | null | undefined; when: (v: string | null | undefined) => string }) {
  if (!at) return <span className="muted">never</span>;
  return (
    <span title={when(at)}>
      {lastUsedText(at)} <span className="muted small">· {when(at)}</span>
    </span>
  );
}
